import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Ban,
  FileText,
  Loader2,
  MapPin,
  PackageCheck,
  Receipt,
  Send,
  Truck,
  Wallet,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission, useAuthStore } from '@/features/auth/auth.store';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ORDER_STATUS, PAYMENT_TYPE, statusMeta } from './order-enums';
import { OrderReceiptDialog } from './order-receipt-dialog';
import {
  useChangeToDelivery,
  useChangeToPickup,
  useDeliveryLocations,
  useForceCapture,
  useOrderItems,
  useSetDriverInstructions,
  useToggleServiceFee,
  useUpdateOrder,
} from './orders.api';
import type { Order } from './types';

/**
 * Everything that can be done to an order.
 *
 * The original packed these into a 1,486-line drawer where each button carried
 * its own inline axios call, its own notification block and a hand-written
 * disabled expression listing status codes. The disable rules are named
 * predicates here so they can be read.
 */

const STATUS = {
  placed: 0,
  deliveryAssigned: 2,
  accepted: 3,
  ready: 4,
  deliveryStarted: 5,
  pickedUp: 6,
  delivered: 7,
  storeCancelled: 8,
  userCancelled: 9,
  pickingStarted: 16,
  pickingFinished: 17,
  packingStarted: 18,
  pendingDriver: 21,
} as const;

const TYPE_PICKUP = 0;
const TYPE_DELIVERY = 1;

/** Reason code the original sent with a dispatch. */
const REASON_DISPATCH = 7;

const CLOSED: number[] = [
  STATUS.pickedUp,
  STATUS.delivered,
  STATUS.storeCancelled,
  STATUS.userCancelled,
];

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  pending,
  variant = 'outline',
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pending?: boolean;
  variant?: 'outline' | 'destructive';
}) {
  return (
    <Button
      variant={variant}
      className="w-full justify-start"
      disabled={disabled || pending}
      onClick={onClick}
    >
      {pending ? <Loader2 className="animate-spin" /> : icon}
      {label}
    </Button>
  );
}

function ItemsTab({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const { data, isPending, isFetching, isError } = useOrderItems(orderId);

  if (isPending) {
    return (
      <div className="divide-y rounded-lg border">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-4 p-3">
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-4 w-14" />
          </div>
        ))}
      </div>
    );
  }

  if (isError || !data?.length) {
    return (
      <p className="text-muted-foreground py-10 text-center text-sm">
        {isError ? t('orders.itemsFailed') : t('orders.noItems')}
      </p>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table busy={isFetching}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('orders.item')}</TableHead>
            <TableHead className="text-right">{t('orders.quantity')}</TableHead>
            <TableHead className="text-right">{t('orders.price')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id} className={cn(item.isVoided && 'opacity-50')}>
              <TableCell>
                <p className="font-medium">{item.name || item.completeName || '—'}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {[item.brand, item.upc].filter(Boolean).join(' · ')}
                </p>
                {item.isVoided && (
                  <Badge variant="destructive" className="mt-1">
                    {t('orders.voided')}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatNumber(item.amount)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCurrency(item.extendedPrice ?? item.price)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function OrderActionsSheet({
  order,
  open,
  onOpenChange,
}: {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isAdmin = usePermission('isSuperAdmin');
  const isCustomerService = usePermission('isCustomerService');
  const approverId = useAuthStore((s) => s.profile?.id ?? '');

  const [newStatus, setNewStatus] = useState<string>('');
  const [newPayment, setNewPayment] = useState<string>('');
  const [instructions, setInstructions] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [pickingLocation, setPickingLocation] = useState(false);

  const update = useUpdateOrder();
  const toggleFee = useToggleServiceFee();
  const forceCapture = useForceCapture();
  const setDriverInstructions = useSetDriverInstructions();
  const changeToPickup = useChangeToPickup();
  const changeToDelivery = useChangeToDelivery();

  // Changing a pickup to a delivery needs one of the customer's addresses that
  // this store can actually reach — the endpoint keys on the store's primary
  // location, not the order's.
  const { data: businesses } = useBusinesses();
  const storeLocationId =
    businesses?.find((business) => business.id === order?.businessId)
      ?.locations?.[0]?.id ?? null;

  const deliveryLocations = useDeliveryLocations({
    customerId: order?.customerId ?? null,
    businessLocationId: storeLocationId,
    enabled: pickingLocation,
  });

  if (!order) return null;

  const status = order.status ?? -1;
  const isClosed = CLOSED.includes(status);
  const isDelivery = order.type === TYPE_DELIVERY;

  const ok = (message: string) => () => {
    toast.success(t(message));
    onOpenChange(false);
  };
  const fail = () => toast.error(t('orders.actionFailed'));

  /** The original's dispatch: delivery orders go out, pickups are collected. */
  const dispatchable = ![
    STATUS.delivered,
    STATUS.pickedUp,
    STATUS.storeCancelled,
    STATUS.userCancelled,
    STATUS.accepted,
    STATUS.deliveryStarted,
    STATUS.pickingStarted,
    STATUS.pickingFinished,
    STATUS.packingStarted,
    STATUS.pendingDriver,
  ].includes(status as never);

  const cancellable =
    isCustomerService || !(order.hasBeenCharged || isClosed);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b">
          <SheetTitle className="flex items-center gap-2">
            {t('orders.actions')}
            <Badge variant={statusMeta(order.status).tone}>
              {t(statusMeta(order.status).labelKey)}
            </Badge>
          </SheetTitle>
          <SheetDescription className="font-mono">
            {order.confirmationCode} · {formatCurrency(order.orderTotal)}
          </SheetDescription>
        </SheetHeader>

        <div className="p-4">
          <Tabs defaultValue="actions">
            <TabsList>
              <TabsTrigger value="actions">{t('orders.actions')}</TabsTrigger>
              <TabsTrigger value="items">{t('orders.items')}</TabsTrigger>
            </TabsList>

            <TabsContent value="actions" className="space-y-5">
              {/* -------------------------------------------- documents */}
              <section className="space-y-2">
                <ActionButton
                  icon={<Receipt />}
                  label={t('orders.receipt')}
                  onClick={() => setShowReceipt(true)}
                />
                <ActionButton
                  icon={<FileText />}
                  label={t('orders.terminalData')}
                  // The original disabled this on an empty array; the field is
                  // absent entirely on non-POS orders.
                  disabled={
                    !Array.isArray(order.terminalData) ||
                    order.terminalData.length === 0
                  }
                  onClick={() => setShowTerminal((value) => !value)}
                />
                {showTerminal && Array.isArray(order.terminalData) && (
                  <pre className="bg-muted max-h-56 overflow-auto rounded-lg p-3 text-xs">
                    {JSON.stringify(order.terminalData, null, 2)}
                  </pre>
                )}
              </section>

              {/* ----------------------------------------- fulfilment */}
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('orders.fulfilment')}
                </h3>

                <ActionButton
                  icon={<Send />}
                  label={t('orders.dispatch')}
                  disabled={!dispatchable}
                  pending={update.isPending}
                  onClick={() =>
                    update.mutate(
                      {
                        OrderId: order.id,
                        ConfirmationCode: order.confirmationCode,
                        Status: isDelivery
                          ? STATUS.deliveryStarted
                          : STATUS.pickedUp,
                        ApproverId: approverId,
                        Reason: REASON_DISPATCH,
                      },
                      { onSuccess: ok('orders.dispatched'), onError: fail }
                    )
                  }
                />

                {/* Delivery only, and only once the driver is on the way —
                    a pickup is never "delivered". The original hid the button
                    entirely for pickups rather than showing it disabled. */}
                {isDelivery && (
                  <ActionButton
                    icon={<PackageCheck />}
                    label={t('orders.markDelivered')}
                    disabled={status !== STATUS.deliveryStarted}
                    pending={update.isPending}
                    onClick={() =>
                      update.mutate(
                        {
                          OrderId: order.id,
                          ConfirmationCode: order.confirmationCode,
                          Status: STATUS.delivered,
                          ApproverId: approverId,
                          Reason: REASON_DISPATCH,
                        },
                        {
                          onSuccess: ok('orders.markedDelivered'),
                          onError: fail,
                        }
                      )
                    }
                  />
                )}

                {isDelivery && (
                  <div className="flex gap-2">
                    <Input
                      value={instructions}
                      onChange={(event) => setInstructions(event.target.value)}
                      placeholder={t('orders.driverInstructions')}
                    />
                    <Button
                      variant="outline"
                      disabled={
                        !instructions.trim() || setDriverInstructions.isPending
                      }
                      onClick={() =>
                        setDriverInstructions.mutate(
                          { OrderId: order.id, Instructions: instructions },
                          {
                            onSuccess: () => {
                              toast.success(t('orders.instructionsSaved'));
                              setInstructions('');
                            },
                            onError: fail,
                          }
                        )
                      }
                    >
                      {setDriverInstructions.isPending ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        t('common.save')
                      )}
                    </Button>
                  </div>
                )}
              </section>

              {/* --------------------------------------- service type */}
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('orders.serviceType')}
                </h3>

                {/* One button whose direction depends on the current type —
                    a pickup becomes a delivery, a delivery becomes a pickup.
                    Only these two convert; in-store orders have no counterpart. */}
                {order.type === TYPE_DELIVERY && (
                  <ActionButton
                    icon={<Truck />}
                    label={t('orders.changeToPickup')}
                    disabled={isClosed}
                    pending={changeToPickup.isPending}
                    onClick={() =>
                      changeToPickup.mutate(order.id, {
                        onSuccess: ok('orders.changedToPickup'),
                        onError: fail,
                      })
                    }
                  />
                )}

                {order.type === TYPE_PICKUP && (
                  <>
                    <ActionButton
                      icon={<Truck />}
                      label={t('orders.changeToDelivery')}
                      disabled={isClosed}
                      onClick={() => setPickingLocation((value) => !value)}
                    />

                    {pickingLocation && (
                      <div className="space-y-2 rounded-lg border p-3">
                        <p className="text-muted-foreground text-xs">
                          {t('orders.chooseAddress')}
                        </p>

                        {/* `isLoading`: the query waits on the store's
                            location, and is pending forever without one. */}
                        {deliveryLocations.isLoading && (
                          <div className="text-muted-foreground flex justify-center py-4">
                            <Loader2 className="size-4 animate-spin" />
                          </div>
                        )}

                        {deliveryLocations.isError && (
                          <p className="text-destructive text-xs">
                            {t('orders.addressesFailed')}
                          </p>
                        )}

                        {deliveryLocations.data?.length === 0 && (
                          <p className="text-muted-foreground text-xs">
                            {t('orders.noDeliverableAddresses')}
                          </p>
                        )}

                        {deliveryLocations.data?.map((location) => (
                          <button
                            key={location.id}
                            type="button"
                            disabled={changeToDelivery.isPending}
                            onClick={() =>
                              changeToDelivery.mutate(
                                { orderId: order.id, locationId: location.id },
                                {
                                  onSuccess: ok('orders.changedToDelivery'),
                                  onError: fail,
                                }
                              )
                            }
                            className="hover:bg-accent focus-visible:ring-ring/50 flex w-full items-start gap-2 rounded-md p-2 text-left text-sm outline-none focus-visible:ring-2"
                          >
                            <MapPin className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                            <span className="min-w-0">
                              {location.name && (
                                <span className="block font-medium">
                                  {location.name}
                                </span>
                              )}
                              <span className="block truncate">
                                {location.addressLine}
                              </span>
                              <span className="text-muted-foreground block text-xs">
                                {[location.city, location.zipcode]
                                  .filter(Boolean)
                                  .join(', ')}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* ---------------------------------------------- status */}
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('orders.changeStatus')}
                </h3>
                <div className="flex gap-2">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t('orders.selectStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORDER_STATUS).map(([value, meta]) => (
                        <SelectItem key={value} value={value}>
                          {t(meta.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    disabled={!newStatus || update.isPending}
                    onClick={() =>
                      update.mutate(
                        {
                          OrderId: order.id,
                          ConfirmationCode: order.confirmationCode,
                          Status: Number(newStatus),
                          ApproverId: approverId,
                        },
                        { onSuccess: ok('orders.statusChanged'), onError: fail }
                      )
                    }
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </section>

              {/* --------------------------------------------- payment */}
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('orders.payment')}
                </h3>

                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">
                    {t('orders.changePaymentMethod')}
                  </Label>
                  <div className="flex gap-2">
                    <Select value={newPayment} onValueChange={setNewPayment}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t('orders.selectPayment')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PAYMENT_TYPE).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      disabled={!newPayment || isClosed || update.isPending}
                      onClick={() =>
                        update.mutate(
                          {
                            OrderId: order.id,
                            ConfirmationCode: order.confirmationCode,
                            PaymentType: Number(newPayment),
                          },
                          {
                            onSuccess: ok('orders.paymentChanged'),
                            onError: fail,
                          }
                        )
                      }
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>

                <ActionButton
                  icon={<Receipt />}
                  label={t('orders.toggleServiceFee')}
                  pending={toggleFee.isPending}
                  onClick={() =>
                    toggleFee.mutate(order.id, {
                      onSuccess: ok('orders.serviceFeeToggled'),
                      onError: fail,
                    })
                  }
                />

                {isAdmin && order.confirmationCode && (
                  <ActionButton
                    icon={<Wallet />}
                    label={t('orders.forceCapture')}
                    disabled={order.hasBeenCharged}
                    pending={forceCapture.isPending}
                    onClick={() =>
                      forceCapture.mutate(order.confirmationCode!, {
                        onSuccess: ok('orders.captured'),
                        onError: fail,
                      })
                    }
                  />
                )}
              </section>

              {/* ---------------------------------------------- cancel */}
              <section className="space-y-2">
                <h3 className="text-destructive text-xs font-semibold tracking-wider uppercase">
                  {t('customers.dangerSection')}
                </h3>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="w-full justify-start"
                      disabled={!cancellable || update.isPending}
                    >
                      <Ban />
                      {t('orders.cancelOrder')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('orders.cancelConfirmTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('orders.cancelConfirmBody', {
                          code: order.confirmationCode ?? '',
                        })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() =>
                          update.mutate(
                            {
                              OrderId: order.id,
                              ConfirmationCode: order.confirmationCode,
                              Status: STATUS.storeCancelled,
                              ApproverId: approverId,
                            },
                            {
                              onSuccess: ok('orders.cancelled'),
                              onError: fail,
                            }
                          )
                        }
                      >
                        {t('orders.cancelOrder')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </section>

              <p className="text-muted-foreground text-xs">
                {t('orders.moreActionsNote')}
              </p>
            </TabsContent>

            <TabsContent value="items">
              <ItemsTab orderId={order.id} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>

      <OrderReceiptDialog
        order={order}
        open={showReceipt}
        onOpenChange={setShowReceipt}
      />
    </Sheet>
  );
}
